import React, { useMemo, useState } from "react";
import {
  AnalyticsAudience,
  AnalyticsGranularity,
  AnalyticsPlatform,
  ClicksTrendingMetric,
  SearchPreset,
} from "../api/analyticsAdmin";
import { computeDateRange } from "../helpers/analyticsPlus";
import { autoGranularityForFilters } from "../helpers/searchTrend";
import { WidgetBoundary } from "../components/AdminDashboard/WidgetBoundary";
import ActivityNavTabs from "../components/AnalyticsPlus/ActivityNavTabs";
import {
  ClicksFilterBar,
  ClicksFilters,
} from "../components/AnalyticsPlus/ClicksFilterBar";
import ClicksFunnelTrend from "../components/AnalyticsPlus/ClicksFunnelTrend";
import ClicksFunnelOverview from "../components/AnalyticsPlus/ClicksFunnelOverview";
import ClicksBusinessFunnel from "../components/AnalyticsPlus/ClicksBusinessFunnel";
import ClicksTrending from "../components/AnalyticsPlus/ClicksTrending";
import StreamProgress from "../components/AnalyticsPlus/StreamProgress";
import {
  CLICKS_BREAKDOWNS,
  clicksSections,
} from "../components/AnalyticsPlus/clicksSections";
import { useAnalyticsStream } from "../components/AnalyticsPlus/useAnalyticsStream";
import { useDebouncedValue } from "../components/AnalyticsPlus/useDebouncedValue";

const FILTER_SETTLE_MS = 400;

const AdminActivityClicks: React.FC = () => {
  const initialRange = computeDateRange("last30", new Date());
  const [filters, setFilters] = useState<ClicksFilters>({
    preset: "Last30Days",
    from: initialRange.from,
    to: initialRange.to,
    platform: "All",
    audience: "All",
    conversionWindowDays: 4,
  });

  // The widget controls that shape a request live here, because the page owns the
  // batch. Each still re-asks for only its own section: the stream hook compares
  // every section's query and re-requests just the ones that changed.
  const [trendGranularity, setTrendGranularity] =
    useState<AnalyticsGranularity | null>(null);
  const [trendingGranularity, setTrendingGranularity] =
    useState<AnalyticsGranularity | null>(null);
  const [trendingTopN, setTrendingTopN] = useState(10);
  const [trendingMetric, setTrendingMetric] =
    useState<ClicksTrendingMetric>("Opens");

  // The custom-range inputs fire on every keystroke; without this each one would
  // open a stream and abort it, cancelling queries mid-flight on the server.
  const settled = useDebouncedValue(filters, FILTER_SETTLE_MS);

  const autoGranularity = autoGranularityForFilters(settled);
  const sections = useMemo(
    () =>
      clicksSections({
        filters: settled,
        trendGranularity: trendGranularity ?? autoGranularity,
        trendingGranularity: trendingGranularity ?? autoGranularity,
        trendingTopN,
        trendingMetric,
      }),
    [
      settled,
      autoGranularity,
      trendGranularity,
      trendingGranularity,
      trendingTopN,
      trendingMetric,
    ]
  );

  const { state, progress } = useAnalyticsStream(sections);

  const handlePresetChange = (preset: SearchPreset) =>
    setFilters((prev) => ({ ...prev, preset }));

  const handleCustomChange = (
    patch: Partial<Pick<ClicksFilters, "from" | "to">>
  ) => setFilters((prev) => ({ ...prev, ...patch }));

  const handlePlatformChange = (platform: AnalyticsPlatform) =>
    setFilters((prev) => ({ ...prev, platform }));

  const handleAudienceChange = (audience: AnalyticsAudience) =>
    setFilters((prev) => ({ ...prev, audience }));

  const handleConversionWindowChange = (conversionWindowDays: number) =>
    setFilters((prev) => ({ ...prev, conversionWindowDays }));

  return (
    <div>
      <h2 className="font-medium mb-1">Activity</h2>
      <p className="text-sm text-gray-500 mb-6">
        Activity metrics have been collected since 22 June 2026, so earlier
        dates show no data.
      </p>

      <ActivityNavTabs />

      <ClicksFilterBar
        filters={filters}
        onPresetChange={handlePresetChange}
        onCustomChange={handleCustomChange}
        onPlatformChange={handlePlatformChange}
        onAudienceChange={handleAudienceChange}
        onConversionWindowChange={handleConversionWindowChange}
      />

      <StreamProgress progress={progress} />

      {/* Sections now render partial data, so a field that arrives missing throws
          during render — and the app mounts no boundary of its own, which would
          take the sidebar down with the chart. */}
      <WidgetBoundary name="Top businesses">
        <ClicksBusinessFunnel section={state.businessFunnel} />
      </WidgetBoundary>

      <WidgetBoundary name="Trend over time">
        <ClicksFunnelTrend
          section={state.series}
          granularity={trendGranularity ?? autoGranularity}
          onGranularityChange={setTrendGranularity}
        />
      </WidgetBoundary>

      <WidgetBoundary name="Clicks funnel">
        <ClicksFunnelOverview
          headline={state.overview}
          breakdowns={CLICKS_BREAKDOWNS.map((breakdown) => ({
            key: breakdown.key,
            label: breakdown.label,
            section: state[breakdown.id],
          }))}
        />
      </WidgetBoundary>

      <WidgetBoundary name="Trending businesses">
        <ClicksTrending
          section={state.trending}
          granularity={trendingGranularity ?? autoGranularity}
          onGranularityChange={setTrendingGranularity}
          topN={trendingTopN}
          onTopNChange={setTrendingTopN}
          metric={trendingMetric}
          onMetricChange={setTrendingMetric}
        />
      </WidgetBoundary>
    </div>
  );
};

export default AdminActivityClicks;
